import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ShiftTimeEntry } from "@/lib/backend/api";
import { isValidDisputeNote } from "@/lib/shift/employee-hours-response";

interface EmployeeHoursResponseSectionProps {
  entry: ShiftTimeEntry;
  onRespond: (response: "AGREED" | "DISPUTED", note?: string) => void;
  isSubmitting: boolean;
}

/** De gemarkeerde keuze: gevuld, zoals een knop die aan staat. */
const MARKED =
  "border-2 border-solid border-button-primary bg-button-primary text-txt-inverted" as const;

/** De andere keuze: een omlijning, zodat er één gevulde knop op het scherm staat. */
const UNMARKED = "border border-solid border-bordercolor-primary text-txt-primary" as const;

/**
 * "Klopt" / "Klopt niet" bij uren die de organisatie namens de medewerker invoerde (admin#624).
 *
 * De medewerker past hier géén uren aan — dat blijft werk voor de planner. Hij bevestigt of maakt
 * bezwaar; bij bezwaar is een toelichting verplicht, zodat de planner weet wát er niet klopt zonder
 * eerst te moeten appen.
 *
 * Heeft hij al gereageerd, dan toont dit blok die reactie plus de mogelijkheid erop terug te komen
 * — uren worden nog weleens opnieuw bekeken. Die tweede ronde is wat op 28-09-2026 op een toestel
 * misging (admin#1093): "Jouw reactie: Klopt niet" stond in het rood, en eronder was "Klopt" de
 * gevulde knop. De gevulde knop leest als de gemaakte keuze, dus het scherm sprak zichzelf tegen.
 * De opmaak volgt daarom de reactie die er ligt, en zonder reactie is geen van beide gemarkeerd.
 */
export function EmployeeHoursResponseSection({
  entry,
  onRespond,
  isSubmitting,
}: EmployeeHoursResponseSectionProps) {
  const { t } = useTranslation();
  const [isDisputing, setIsDisputing] = useState(false);
  const [note, setNote] = useState("");

  const currentResponse = entry.employeeResponse ?? "NOT_RESPONDED";
  const hasResponded = currentResponse !== "NOT_RESPONDED";
  const canSubmitDispute = isValidDisputeNote(note) && !isSubmitting;

  return (
    <div
      className="flex flex-col gap-3 border-t border-solid border-bordercolor-primary pt-4"
      data-testid="employee-hours-response"
    >
      {hasResponded ? (
        <div className="flex flex-col gap-2">
          <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
            {t("shift:hours.response.yourResponse", {
              defaultValue: "Jouw reactie",
            })}
          </p>
          <div
            data-testid={`employee-hours-response-status-${currentResponse}`}
            className={`w-fit rounded-full px-3 py-1 text-sm font-medium ${
              currentResponse === "AGREED"
                ? "bg-success-100/10 text-success-120"
                : "bg-error-100/10 text-error-120"
            }`}
          >
            {currentResponse === "AGREED"
              ? t("shift:hours.response.agreed", { defaultValue: "Klopt" })
              : t("shift:hours.response.disputed", {
                  defaultValue: "Klopt niet",
                })}
          </div>
          {currentResponse === "DISPUTED" && entry.employeeResponseNote && (
            <p
              className="text-base font-medium leading-[1.5] text-txt-primary"
              data-testid="employee-hours-response-note"
            >
              {entry.employeeResponseNote}
            </p>
          )}
        </div>
      ) : (
        <p className="text-base font-medium leading-[1.5] text-txt-tertiary">
          {t("shift:hours.response.prompt", {
            defaultValue: "Kloppen deze uren?",
          })}
        </p>
      )}

      {isDisputing ? (
        <div className="flex flex-col gap-3">
          {/* De placeholder vraagt om wat de planner nodig heeft. Stond er alleen dat iemand meer
              had gewerkt, dan las admin bij VOLGENS DE MEDEWERKER: geen tijden opgegeven. Er was
              bezwaar, maar niet waartegen. Echte begin- en eindtijdvelden vragen een EMPLOYEE-claim
              en dus api-werk; deze zin haalt het meeste daarvan alvast op. */}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            rows={3}
            data-testid="employee-hours-response-note-input"
            placeholder={t("shift:hours.response.notePlaceholder", {
              defaultValue:
                "Zet erbij hoe laat je echt begon en stopte, en wat er niet klopt. Bijvoorbeeld: ik werkte van 18:00 tot 00:30 en had een kwartier pauze.",
            })}
            className="w-full rounded-xl border border-solid border-bordercolor-primary bg-transparent p-3 text-base leading-[1.5] text-txt-primary"
          />
          <div className="flex gap-2.5">
            <button
              type="button"
              data-testid="employee-hours-response-dispute-cancel"
              onClick={() => {
                setIsDisputing(false);
                setNote("");
              }}
              className="flex-1 rounded-xl border border-solid border-bordercolor-primary py-3 text-center text-base font-semibold text-txt-primary"
            >
              {t("common:cancel", { defaultValue: "Annuleren" })}
            </button>
            <button
              type="button"
              data-testid="employee-hours-response-dispute-submit"
              disabled={!canSubmitDispute}
              onClick={() => {
                onRespond("DISPUTED", note);
                setIsDisputing(false);
                setNote("");
              }}
              className="flex-1 rounded-xl border-2 border-solid border-button-primary bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:opacity-50"
            >
              {/* Ligt er al een reactie, dan verstuur je geen nieuwe maar verving je de oude. */}
              {hasResponded
                ? t("shift:hours.response.update", { defaultValue: "Reactie bijwerken" })
                : t("shift:hours.response.send", { defaultValue: "Versturen" })}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* De knoppen blijven staan na een reactie, want een toelichting bijstellen is precies
              wat mensen willen. Maar zonder deze kop leest het als een eerste keer reageren. */}
          {hasResponded && (
            <p className="text-sm leading-[1.4] text-txt-secondary">
              {t("shift:hours.response.updatePrompt", {
                defaultValue: "Je reactie bijwerken",
              })}
            </p>
          )}
          <div className="flex gap-2.5">
            <button
              type="button"
              data-testid="employee-hours-response-agree"
              disabled={isSubmitting}
              // De gekozen optie is gemarkeerd, de andere niet. Zonder reactie geen van beide: er
              // is dan nog niets gekozen, en dat mag het scherm niet suggereren.
              aria-pressed={currentResponse === "AGREED"}
              onClick={() => onRespond("AGREED")}
              className={`flex-1 rounded-xl py-3 text-center text-base font-semibold disabled:opacity-50 ${
                currentResponse === "AGREED" ? MARKED : UNMARKED
              }`}
            >
              {t("shift:hours.response.agreeAction", { defaultValue: "Klopt" })}
            </button>
            <button
              type="button"
              data-testid="employee-hours-response-dispute"
              disabled={isSubmitting}
              aria-pressed={currentResponse === "DISPUTED"}
              onClick={() => {
                // Met de vorige toelichting erin: bijstellen vraagt niet dat je hem opnieuw typt.
                setNote(entry.employeeResponseNote ?? "");
                setIsDisputing(true);
              }}
              className={`flex-1 rounded-xl py-3 text-center text-base font-semibold disabled:opacity-50 ${
                currentResponse === "DISPUTED" ? MARKED : UNMARKED
              }`}
            >
              {t("shift:hours.response.disputeAction", {
                defaultValue: "Klopt niet",
              })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
